import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiClient } from '@/lib/api-client'
import { toastStrings } from '@/lib/toasts'
import type {
  ProtocolTemplateDto,
  CreateProtocolTemplateDto,
  UpdateProtocolTemplateDto,
} from '@rezeta/shared'

const QK = 'protocol-templates'

export function useProtocolTemplates(): UseQueryResult<ProtocolTemplateDto[], Error> {
  return useQuery({
    queryKey: [QK],
    queryFn: () => apiClient.get<ProtocolTemplateDto[]>('/v1/protocol-templates'),
  })
}

export function useProtocolTemplate(id: string): UseQueryResult<ProtocolTemplateDto, Error> {
  return useQuery({
    queryKey: [QK, id],
    queryFn: () => apiClient.get<ProtocolTemplateDto>(`/v1/protocol-templates/${id}`),
    enabled: !!id,
  })
}

export function useCreateProtocolTemplate(): UseMutationResult<
  ProtocolTemplateDto,
  Error,
  CreateProtocolTemplateDto
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dto: CreateProtocolTemplateDto) =>
      apiClient.post<ProtocolTemplateDto>('/v1/protocol-templates', dto),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [QK] })
      toast.success(toastStrings.templateCreated)
    },
    onError: () => {
      toast.error(toastStrings.errorTemplateSave)
    },
  })
}

export function useUpdateProtocolTemplate(
  id: string,
): UseMutationResult<ProtocolTemplateDto, Error, UpdateProtocolTemplateDto> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dto: UpdateProtocolTemplateDto) =>
      apiClient.patch<ProtocolTemplateDto>(`/v1/protocol-templates/${id}`, dto),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [QK] })
      void qc.invalidateQueries({ queryKey: [QK, id] })
      toast.success(toastStrings.templateUpdated)
    },
    onError: () => {
      toast.error(toastStrings.errorTemplateSave)
    },
  })
}

export function useDeleteProtocolTemplate(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/v1/protocol-templates/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [QK] })
      toast.success(toastStrings.templateDeleted)
    },
    onError: () => {
      toast.error(toastStrings.errorTemplateSave)
    },
  })
}
